import { useState, useEffect } from 'preact/hooks';
import { ChevronDown, ChevronRight } from 'lucide-preact';
import { useFilter } from '../contexts/FilterContext';
import { 
  fetchStatementData, 
  extractAvailablePeriods,
  filterStatementDataByPeriod 
} from '../functions/calculations/statementData';
import cashflowLayout from '../financialLayouts/cashflowLayout.json';
import incomeStatementLayout from '../financialLayouts/incomeStatementLayout.json';
import balanceSheetLayout from '../financialLayouts/balanceSheetLayout.json';
import '../css/statement.css';

type StatementType = 'cashflow' | 'income' | 'balance';

interface StatementProps {
  statementType?: StatementType;
  selectedPeriod?: string; // Date string or 'all' for all periods
  onPeriodsChange?: (periods: string[]) => void; // Callback to pass available periods to parent
  onFilteredDataChange?: (data: Map<number, Map<string, number>>) => void; // Callback to pass filtered data for charts
  valueMode?: 'category' | 'portioned'; // Toggle between category_value and portioned_value
}

interface CategoryNode {
  properties: Array<{
    category_code: number;
    parent_code: number;
    has_children: number;
    highlight: number;
  }>;
  children?: Record<string, CategoryNode>;
}

type LayoutData = Record<string, CategoryNode>;

const layouts: Record<StatementType, LayoutData> = {
  cashflow: cashflowLayout as LayoutData,
  income: incomeStatementLayout as LayoutData,
  balance: balanceSheetLayout as LayoutData
};

// Format date from YYYYMM or YYYY-MM-DD to "MMM YYYY" format
const formatDateToMonthYear = (dateStr: string): string => {
  // Handle YYYYMM format
  if (dateStr.length === 6 && /^\d{6}$/.test(dateStr)) {
    const year = dateStr.substring(0, 4);
    const month = parseInt(dateStr.substring(4, 6), 10);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[month - 1]} ${year}`;
  }
  
  // Handle YYYY-MM-DD format
  if (dateStr.length === 10 && dateStr.includes('-')) {
    const [year, month] = dateStr.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month, 10) - 1]} ${year}`;
  }
  
  return dateStr;
};

const formatCurrency = (value: number): string => {
  if (value === 0) return '0.00';
  const absValue = Math.abs(value);
  const formatted = absValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return value < 0 ? `(${formatted})` : formatted;
};

interface RowData {
  category: string;
  level: number;
  isExpanded: boolean;
  hasChildren: boolean;
  isHighlighted: boolean;
  values: number[];
  node: CategoryNode;
  path: string;
}

export const Statement = ({ 
  statementType = 'cashflow', 
  selectedPeriod = 'all',
  onPeriodsChange,
  onFilteredDataChange,
  valueMode = 'portioned'
}: StatementProps) => {
  const { selectedClient, selectedUnit } = useFilter();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [allStatementData, setAllStatementData] = useState<Map<number, Map<string, number>>>(new Map());
  const [statementData, setStatementData] = useState<Map<number, Map<string, number>>>(new Map());
  const [months, setMonths] = useState<string[]>([]);
  const [monthToDateMap, setMonthToDateMap] = useState<Map<string, string>>(new Map());
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const layout = layouts[statementType];

  // Function to initialize all rows as expanded
  const initializeExpandedRows = (layoutData: LayoutData): Set<string> => {
    const initialExpanded = new Set<string>();
    
    const addExpandedPaths = (data: LayoutData, parentPath: string = '') => {
      for (const [categoryName, node] of Object.entries(data)) {
        const categoryPath = parentPath ? `${parentPath}::${categoryName}` : categoryName;
        const props = node.properties[0];
        if (props.has_children === 1) {
          initialExpanded.add(categoryPath);
          if (node.children) {
            addExpandedPaths(node.children, categoryPath);
          }
        }
      }
    };
    
    addExpandedPaths(layoutData);
    return initialExpanded;
  };

  // Fetch statement data when statementType, user, or unit changes
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedClient?.user_id_no || !selectedUnit?.unit_id_no) {
        setAllStatementData(new Map());
        setStatementData(new Map());
        setMonths([]);
        setMonthToDateMap(new Map());
        setAvailablePeriods([]);
        onPeriodsChange?.([]);
        onFilteredDataChange?.(new Map());
        return;
      }

      setLoading(true);
      try {
        const data = await fetchStatementData(
          statementType,
          selectedClient.user_id_no,
          selectedUnit.unit_id_no,
          valueMode
        );
        
        setAllStatementData(data);
        
        // Extract available periods
        const periods = extractAvailablePeriods(data);
        setAvailablePeriods(periods);
        onPeriodsChange?.(periods);
      } catch (error) {
        console.error('Error fetching statement data:', error);
        setAllStatementData(new Map());
        setStatementData(new Map());
        setMonths([]);
        setMonthToDateMap(new Map());
        setAvailablePeriods([]);
        onPeriodsChange?.([]);
        onFilteredDataChange?.(new Map());
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [statementType, selectedClient?.user_id_no, selectedUnit?.unit_id_no, valueMode]);
  
  // Update filtered data when selectedPeriod changes or when data is first loaded
  useEffect(() => {
    if (allStatementData.size > 0 && availablePeriods.length > 0) {
      const filteredData = filterStatementDataByPeriod(allStatementData, selectedPeriod);
      setStatementData(filteredData);
      onFilteredDataChange?.(filteredData);

      // Extract all dates from filtered data (these are the actual dates, not years)
      const allDates = new Set<string>();
      for (const dateMap of filteredData.values()) {
        for (const date of dateMap.keys()) {
          allDates.add(date);
        }
      }

      const sortedDates = Array.from(allDates).sort();
      const formattedMonths = sortedDates.map(formatDateToMonthYear);
      setMonths(formattedMonths);

      // Update date map
      const dateMap = new Map<string, string>();
      sortedDates.forEach(date => {
        const formatted = formatDateToMonthYear(date);
        dateMap.set(formatted, date);
      });
      setMonthToDateMap(dateMap);
    }
  }, [selectedPeriod, allStatementData, availablePeriods, onFilteredDataChange]);

  // Initialize expanded rows on mount and when statementType changes
  useEffect(() => {
    const expanded = initializeExpandedRows(layout);
    setExpandedRows(expanded);
  }, [statementType, layout]);

  const toggleRow = (categoryPath: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(categoryPath)) {
      newExpanded.delete(categoryPath);
    } else {
      newExpanded.add(categoryPath);
    }
    setExpandedRows(newExpanded);
  };


  const buildRows = (
    data: LayoutData,
    level: number = 0,
    parentPath: string = ''
  ): RowData[] => {
    const rows: RowData[] = [];

    for (const [categoryName, node] of Object.entries(data)) {
      const categoryPath = parentPath ? `${parentPath}::${categoryName}` : categoryName;
      const props = node.properties[0];
      const hasChildren = props.has_children === 1 && node.children;
      const isExpanded = expandedRows.has(categoryPath);
      const isHighlighted = props.highlight === 1;

      // Get real values from API data for this category
      const categoryCode = props.category_code;
      const categoryData = statementData.get(categoryCode);
      const values = months.map(month => {
        // Get the original date string for this formatted month
        const originalDate = monthToDateMap.get(month);
        if (!originalDate || !categoryData) return 0;
        
        return categoryData.get(originalDate) || 0;
      });

      rows.push({
        category: categoryName,
        level,
        isExpanded,
        hasChildren: !!hasChildren,
        isHighlighted,
        values,
        node,
        path: categoryPath
      });

      // Add children if expanded and has children
      if (isExpanded && hasChildren && node.children) {
        const childRows = buildRows(node.children, level + 1, categoryPath);
        rows.push(...childRows);
      }
    }

    return rows;
  };

  const getCategoryHeader = (): string => {
    switch (statementType) {
      case 'cashflow':
        return 'CASH FLOW CATEGORIES';
      case 'income':
        return 'INCOME STATEMENT CATEGORIES';
      case 'balance':
        return 'BALANCE SHEET CATEGORIES';
      default:
        return 'CASH FLOW CATEGORIES';
    }
  };

  if (loading) {
    return (
      <div className="statement-container">
        <div className="statement-table-wrapper">
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
            Loading statement data...
          </div>
        </div>
      </div>
    );
  }

  if (!selectedClient?.user_id_no || !selectedUnit?.unit_id_no) {
    return (
      <div className="statement-container">
        <div className="statement-table-wrapper">
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
            Please select a client and unit to view statement data.
          </div>
        </div>
      </div>
    );
  }

  const rows = buildRows(layout);

  return (
    <div className="statement-container">
      <div className="statement-table-wrapper">
        <table className="statement-table">
          <thead>
            <tr>
              <th className="statement-category-header">{getCategoryHeader()}</th>
              {months.length > 0 ? (
                months.map((month) => (
                  <th key={month} className="statement-month-header">{month}</th>
                ))
              ) : (
                <th className="statement-month-header">No data available</th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={`${row.category}-${index}`}
                className={`statement-row ${row.isHighlighted ? 'highlighted' : ''} level-${row.level}`}
              >
                <td className="statement-category-cell">
                  <div className="category-content" style={{ paddingLeft: `${row.level * 1.2}rem` }}>
                    {row.hasChildren ? (
                      <button
                        className="expand-button"
                        onClick={() => toggleRow(row.path)}
                      >
                        {row.isExpanded ? (
                          <ChevronDown size={14} />
                        ) : (
                          <ChevronRight size={14} />
                        )}
                      </button>
                    ) : (
                      <span className="expand-spacer"></span>
                    )}
                    <span className={`category-name ${row.isHighlighted ? 'bold' : ''}`}>
                      {row.category}
                    </span>
                  </div>
                </td>
                {row.values.map((value, valueIndex) => (
                  <td key={valueIndex} className="statement-value-cell">
                    {formatCurrency(value)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

